import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { logEvent } from 'common/helpers/internalAnalytics';
import { getCartTitle } from 'common/helpers/miscHelpers';
import { currencyFormat } from 'common/helpers/numberHelper';
import Image from 'products/common/components/Image';
import { addCartItem } from 'redux/cart/actions';
import { useLocalization } from 'redux/localization/utils/hooks';
import useAnalytics from 'utils/hooks/useAnalytics';

const DynamicProductUpsell = (props) => {
  const { carrot } = props;
  const dispatch = useDispatch();
  const { t } = useLocalization();
  const [hasBeenAdded, setHasBeenAdded] = useState(false);

  const settings = useSelector((state) => state.setting.data);
  const { currency } = settings;

  const { product } = carrot;

  useAnalytics(
    () =>
      !!product &&
      !!product.variants?.[0] &&
      !hasBeenAdded &&
      carrot?.personalizationEngine === 'v2',
    'AI Carrot Upsell',
    'Rendered'
  );

  useAnalytics(
    () =>
      !!product &&
      !!product.variants?.[0] &&
      !hasBeenAdded &&
      carrot?.personalizationEngine !== 'v2',
    'Carrot Upsell',
    'Rendered'
  );

  if (!product) {
    return null;
  }

  const variant = product.variants?.[0];

  if (!variant) {
    return null;
  }

  if (hasBeenAdded) {
    return null;
  }

  const handleAddToCart = () => {
    if (carrot?.personalizationEngine === 'v2') {
      logEvent('AI Carrot Upsell', 'Clicked');
    } else {
      logEvent('Carrot Upsell', 'Clicked');
    }

    const item = {
      quantity: 1,
      sku: variant.sku
    };

    const onSuccess = () => {
      setHasBeenAdded(true);
    };

    const onError = () => {
      setHasBeenAdded(false);
    };

    dispatch(addCartItem(item, onSuccess, onError));
  };

  return (
    <div className="c7-carrot c7-carrot--product-upsell">
      {carrot.content?.length > 0 && (
        <div
          className="c7-carrot__message"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: carrot.content }}
        />
      )}
      <div className="c7-carrot__product">
        <div className="c7-carrot__product__image">
          <Image
            product={product}
            imageDisplay="One"
            size="small"
            linkType="None"
            index={0}
          />
        </div>
        <div className="c7-carrot__product__details">
          <div className="c7-carrot__product__details__title">
            {product.title}
          </div>
          <div className="c7-carrot__product__details__variant c7-alt-text">
            {variant.title}
          </div>
          <div className="c7-carrot__product__details__price">
            <span className="c7-sr-only">
              {t('general.accessibility.itemPrice')}
            </span>
            <Price variant={variant} currency={currency} />
          </div>
          <button
            className="c7-btn c7-btn--primary--small"
            type="button"
            onClick={handleAddToCart}
          >
            {t('products.general.addToCart', {
              cartTitle: getCartTitle(settings, t)
            })}
            <span className="c7-sr-only">, {product.title}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const Price = (props) => {
  const { variant, currency } = props;
  const { t } = useLocalization();

  const effectivePrice = variant.addToCartPrice ?? variant.price;
  const comparePrice = variant.comparePrice || variant.price;

  if (comparePrice > effectivePrice) {
    return (
      <>
        <span className="c7-price--original">
          <span className="c7-sr-only">{t('general.accessibility.was')} </span>
          {currencyFormat(comparePrice, currency)}
        </span>
        <span className="c7-price--discounted">
          <span className="c7-sr-only">{t('general.accessibility.now')} </span>
          {currencyFormat(effectivePrice, currency)}
        </span>
      </>
    );
  }

  return currencyFormat(effectivePrice, currency);
};

export default DynamicProductUpsell;
